import React, { useEffect, useState } from 'react';
import Button from '../../components/shared/custom-btn';
import CustomInput from '../../components/shared/custom-input';
import { RxCross2 } from 'react-icons/rx';
import ModalHeading from '../../components/shared/modal-heading';
import { formatPhoneNumber } from '../../utils/format-phone-utils';
import { countryCodes } from '../../data';
import { createUser } from '../../services/users-service';
import CustomReactSelect from '../../components/shared/custom-react-selector';
import { prependDefault } from '../../utils/structure-options-utils';
import Loader from '../../components/shared/loader';

const AddModal = ({ handleCloseAddModal, loadingGlobalData, fetchGlobalData, onAdd, accountId, openEditModal, roles }) => {
    const [countryCode, setCountryCode] = useState('+1');
    const [localPhoneNumber, setLocalPhoneNumber] = useState('');
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        msisdn: '',
        role: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (e) => {
        const rawValue = e.target.value;
        const formatted = formatPhoneNumber(rawValue, countryCode);
        setLocalPhoneNumber(formatted);

        // Combine country code with digits only when saving to formData (without + symbol)
        const digitsOnly = rawValue.replace(/\D/g, '');
        const countryCodeWithoutPlus = countryCode.replace('+', '');
        handleChange({ target: { name: 'msisdn', value: `${countryCodeWithoutPlus}${digitsOnly}` } });
    };

    const handleCountryCodeChange = (e) => {
        const newCode = e.target.value;
        setCountryCode(newCode);

        // Update the form data with new country code (without + symbol)
        const digitsOnly = localPhoneNumber.replace(/\D/g, '');
        const countryCodeWithoutPlus = newCode.replace('+', '');
        handleChange({ target: { name: 'msisdn', value: `${countryCodeWithoutPlus}${digitsOnly}` } });
    };

    // Handle select changes for dropdowns
    const handleSelectChange = (name, option) => {
        setFormData((prev) => ({ ...prev, [name]: option ? option.value : '' }));
    };

    // Handle form submission
    const handleSave = async (e) => {
        e.preventDefault();
        if (isLoading || successMessage) return;
        setIsLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            console.log(formData)
            const response = await createUser(formData, accountId);
            console.log(response.data.data)
            if (response.success) {
                const newItem = {
                    ...response?.data?.data,
                };
                setSuccessMessage('User added successfully!');
                setTimeout(() => {
                    onAdd?.(newItem);
                    openEditModal?.(newItem);
                    handleCloseAddModal();
                }, 1000);
            } else {
                setErrorMessage(response.data.message || 'Failed to add user');
            }
        } catch (error) {
            console.error('Error adding user:', error);
            setErrorMessage('An error occurred while adding the user');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchGlobalData();
    }, [])

    const roleOptions = prependDefault(roles.map(v => ({ value: v.slug, label: v.label })), 'Role');

    return (
        <form onSubmit={handleSave} className="space-y-2 pt-3 pb-3 relative">
            <ModalHeading className="text-left mb-3 border-b border-gray-300 px-4 pb-2">Add User Details</ModalHeading>

            <button
                className="hover:scale-105 absolute -top-2 right-2 bg-white border p-2 rounded-full"
                onClick={handleCloseAddModal}
            >
                <RxCross2 className="text-xl" />
            </button>

            {successMessage && (
                <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
                    {successMessage}
                </div>
            )}

            {errorMessage && (
                <div className="mb-4 px-4 py-2 bg-red-100 text-red-800 rounded-md text-sm text-center">
                    {errorMessage}
                </div>
            )}

            {
                loadingGlobalData ?
                    <Loader />
                    :
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 px-4 gap-y-1.5 gap-x-2">
                            <CustomInput
                                label="First Name"
                                name="firstName"
                                type="text"
                                inputType="text"
                                value={formData.firstName}
                                onChange={handleChange}
                                inputStyle="!py-[3px]"
                                labelGap={'!gap-0.5'}
                                labelStyle={'text-gray-700'}
                                required={true}
                                disabled={isLoading}
                            />
                            <CustomInput
                                label="Last Name"
                                name="lastName"
                                type="text"
                                inputType="text"
                                value={formData.lastName}
                                onChange={handleChange}
                                inputStyle="!py-[3px]"
                                labelGap={'!gap-0.5'}
                                labelStyle={'text-gray-700'}
                                required={true}
                                disabled={isLoading}
                            />
                            <CustomInput
                                label="Email"
                                name="email"
                                type="email"
                                inputType="email"
                                value={formData.email}
                                onChange={handleChange}
                                inputStyle="!py-[3px]"
                                labelGap={'!gap-0.5'}
                                labelStyle={'text-gray-700'}
                                required={true}
                                disabled={isLoading}
                            />
                            <CustomReactSelect
                                label="Role"
                                name="role"
                                value={roleOptions.find((option) => option.value === formData.role) || null}
                                onChange={(option) => handleSelectChange('role', option)}
                                options={roleOptions}
                                placeholder='Select role'
                                required={true}
                                isDisabled={isLoading}
                            />
                            <div className="flex items-center gap-2">
                                <div className="flex flex-col gap-0.5 w-fit">
                                    <label htmlFor="countryCode" className="whitespace-nowrap text-gray-700 text-sm">
                                        Code
                                    </label>
                                    <select
                                        name="countryCode"
                                        value={countryCode}
                                        onChange={handleCountryCodeChange}
                                        disabled={isLoading}
                                        className="h-full xs:w-20 outline-none w-24 !text-xs py-[4px] border-2 border-border bg-white rounded-xl"
                                    >
                                        {countryCodes.map(({ code, name }) => (
                                            <option key={`${code}-${name}`} value={code}>{`${code} (${name})`}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex-grow w-full">
                                    <CustomInput
                                        label="Mobile Number"
                                        name="mobile"
                                        type="tel"
                                        value={localPhoneNumber}
                                        onChange={handlePhoneChange}
                                        inputStyle="border border-border !text-sm !py-2 bg-white rounded-lg"
                                        placeholder="e.g. 123-456-7890"
                                        labelGap="!gap-0.5"
                                        labelStyle="text-gray-700"
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                        </div>

                        <div className="flex justify-end px-4">
                            <Button
                                label={'Save'}
                                loading={isLoading}
                                loadingLabel='Saving...'
                                type="submit"
                                className={`bg-btnBg rounded-lg !py-1 text-white ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                disabled={isLoading || !!successMessage}
                            />
                        </div>
                    </>
            }


        </form>
    );
};

export default AddModal;